import type { ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CircleDot } from "lucide-react";

interface PolicySection {
  title: string;
  content: string | ReactNode;
}
interface PolicyData {
  entity: string;
  title: string;
  lastUpdated: string;
  sections: PolicySection[];
}

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <div className="container mx-auto px-4 py-12 md:px-6 md:py-20 lg:max-w-4xl">
      <p className="text-base/7 font-semibold text-primary">{policy.entity}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-pretty sm:text-5xl">
        {policy.title}
      </h1>
      <p className="my-4 text-sm text-muted-foreground">Last Updated: {policy.lastUpdated}</p>
      {policy.sections.map((item, i) => (
        <div key={i}>
          <PolicySection n={i + 1} s={item} />
          {i < policy.sections.length - 1 && (
            <div className="mx-auto my-8 h-px w-1/3 bg-muted-foreground" />
          )}
        </div>
      ))}
    </div>
  );
}

const entity = "Flash Biometrics";
const contact = {
  website: "App-Order.com",
  phone: "800-884-7820 Office",
  fax: "800-536-0963 Fax",
  email: "helpdesk@zortssports.com",
  address: "1094 E Sahara Ave, Las Vegas, NV 89104",
};

const policy = {
  entity,
  title: "Privacy Policy",
  lastUpdated: "Nov 4, 2025",
  sections: [
    {
      title: "Introduction",
      content: (
        <div className="space-y-4">
          <p>
            {entity} ("we," "our," or "us") provides identity verification services through our
            online platform. We act as a trusted third-party verifier, allowing businesses ("SAAS
            Customers", or "customers") to confirm the identity of their consenting end-users
            ("end-users"), without directly handling sensitive identity documents.
          </p>
          <p>
            This Privacy Policy explains the different categories of information we collect, and how
            we collect, use, protect, and share information when using our services.
          </p>
        </div>
      ),
    },
    {
      title: "Information We Collect",
      content: (
        <>
          <p>
            We collect different types of information depending on how you interact with our
            platform:
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">
            2.1 Anonymous Website Visitors
          </h3>
          <p className="mt-2">
            For all internet traffic on our services, we record information shared with us by
            browsers and networks:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>HTTP request information, including origin, time, user-agent, etc.</Bullet>
            <Bullet>Passive web analytics data</Bullet>
          </ul>
          <p className="mt-4">
            This information is collected anonymously and is not linked to any personally
            identifiable information.
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">
            2.2 Business Customers (SaaS Users)
          </h3>
          <p className="mt-2">When you create an account as a business customer, we collect:</p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>Email address (required)</Bullet>
            <Bullet>
              Optional additional information you choose to provide during account creation
            </Bullet>
            <Bullet>Session and authentication data through essential cookies</Bullet>
          </ul>

          <h3 className="mt-6 text-lg font-semibold text-foreground">
            2.3 End-Users (Identity Verification Subjects)
          </h3>
          <p className="mt-2">
            In order to provide our essential services, when you submit your identity information
            for verification, we collect:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>Your consent to process this information</Bullet>
            <Bullet>
              Personal identification information (name, date of birth, age, sex, etc.)
            </Bullet>
            <Bullet>
              Identity verification documents (driver's license, birth certificate, passport, or
              other government-issued identification)
            </Bullet>
            <Bullet>
              Photographs used for on-site facial recognition and identification services
            </Bullet>
            <Bullet>Biometric data contained within submitted documents</Bullet>
            <Bullet>Other information provided through our online verification forms</Bullet>
          </ul>
        </>
      ),
    },
    {
      title: "How We Collect Information",
      content: (
        <ul role="list" className="ml-4 max-w-xl space-y-3">
          <Bullet>
            <strong className="font-semibold">Website Analytics:</strong> Automatically collected on
            network and internet traffic to our website
          </Bullet>
          <Bullet>
            <strong className="font-semibold">Account Creation:</strong> Collected through
            registration forms when business customers sign up
          </Bullet>
          <Bullet>
            <strong className="font-semibold">Identity Verification:</strong> Collected through
            secure online forms when end-users submit their information for verification
          </Bullet>
          <Bullet>
            <strong className="font-semibold">Cookies:</strong> Collected automatically through
            essential cookies for authentication and session management
          </Bullet>
        </ul>
      ),
    },
    {
      title: "How We Use Your Information",
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground">4.1 Anonymous Analytics Data</h3>
          <p className="mt-2">
            We use anonymous website traffic data to improve our services, understand user behavior,
            and optimize the platform experience.
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">
            4.2 Business Customer Account Information
          </h3>
          <p className="mt-2">
            We use your account information to provide our core identity verification services,
            including:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>Managing your account and authentication</Bullet>
            <Bullet>Communicating with you about our services</Bullet>
            <Bullet>Providing access to verification results for your end-users</Bullet>
          </ul>

          <h3 className="mt-6 text-lg font-semibold text-foreground">
            4.3 End-User Identity Information
          </h3>
          <p className="mt-2">
            We use personal identification information and documents solely for identity
            verification purposes. Specifically:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>To verify the authenticity of submitted identity documents</Bullet>
            <Bullet>
              To confirm identity attributes (age, name, etc.) requested by the business customer
            </Bullet>
            <Bullet>To provide secure storage of identity documents</Bullet>
            <Bullet>To deliver verification results to the requesting business customer</Bullet>
          </ul>

          <p className="mt-6">
            <strong className="font-semibold">
              We NEVER use end-user identity information for marketing, advertising, or any purpose
              other than identity verification services.
            </strong>
          </p>
        </>
      ),
    },
    {
      title: "How We Share Your Information",
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground">
            5.1 Personal Identity Information and Biometric Data
          </h3>
          <p className="mt-2">
            <strong className="font-semibold">
              We NEVER share personal identity documents or biometric data with anyone.
            </strong>
          </p>
          <p className="mt-2">
            End-user sensitive documents remain securely stored with {entity} and are never
            disclosed to business customers or any third parties. Business customers who you
            specifically authorize can see your verified identity in the form of a Flash ID, which
            confirms that we have verified identity details like your name, without needing to share
            the verification documents with the business customer.
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">5.2 Verification Results</h3>
          <p className="mt-2">
            When an end-user submits their information for verification at the request of a business
            customer:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>
              The business customer receives confirmation of identity attributes (such as name, age,
              and verification status)
            </Bullet>
            <Bullet>
              The business customer does NOT receive access to the underlying identity documents or
              sensitive personal information
            </Bullet>
            <Bullet>
              The end-user authorizes this limited sharing by submitting their information through
              our platform
            </Bullet>
          </ul>

          <h3 className="mt-6 text-lg font-semibold text-foreground">5.3 Anonymized Data</h3>
          <p className="mt-2">
            We may share anonymized, aggregated data that cannot identify individual users with
            third parties for analytics or business purposes.
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">
            5.4 Third-Party Service Providers
          </h3>
          <p className="mt-2">We may use third-party service providers for:</p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>Marketing services</Bullet>
            <Bullet>Analytics services (such as website traffic analysis)</Bullet>
          </ul>
          <p className="mt-4">
            These third-party services may collect data through cookies or referral links. However,
            they do not have access to personal identity information or biometric data.
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">5.5 Legal Requirements</h3>
          <p className="mt-2">
            We may disclose information if required by law, court order, or legal process, or to
            protect the rights, property, or safety of {entity}, our users, or others.
          </p>
        </>
      ),
    },
    {
      title: "Cookies and Tracking Technologies",
      content: (
        <>
          <p>Our website uses essential cookies that are necessary for:</p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>User authentication</Bullet>
            <Bullet>Session management</Bullet>
            <Bullet>Maintaining logged-in status</Bullet>
            <Bullet>Ensuring secure access to user accounts</Bullet>
          </ul>
          <p className="mt-4">
            We do not use cookies for advertising purposes. Third-party analytics or marketing
            services we use may employ their own cookies or tracking technologies as described in
            their respective privacy policies.
          </p>
        </>
      ),
    },
    {
      title: "Data Security",
      content: (
        <>
          <p>
            We take the security of your information seriously and implement industry-standard
            security measures:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-3">
            <Bullet>
              <strong className="font-semibold">Anonymous Data:</strong> Stored on secure servers
              with appropriate access controls
            </Bullet>
            <Bullet>
              <strong className="font-semibold">Sensitive Personal Information:</strong> Encrypted
              both in transit (during transmission) and at rest (during storage)
            </Bullet>
            <Bullet>
              <strong className="font-semibold">Identity Documents:</strong> Stored in encrypted
              format with restricted access limited to authorized verification personnel
            </Bullet>
          </ul>
          <p className="mt-4">
            While we implement strong security measures, no method of transmission or storage is
            100% secure. We cannot guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>
        </>
      ),
    },
    {
      title: "Data Retention and Deletion",
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground">8.1 Retention Periods</h3>
          <p className="mt-2">
            We retain different types of information for varying periods based on the purpose for
            which it was collected:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>
              <strong className="font-semibold">Account Information:</strong> Retained for as long
              as your account remains active with us
            </Bullet>
            <Bullet>
              <strong className="font-semibold">Purchase and Sales Records:</strong> Retained for at
              least 5 years for accounting and tax compliance purposes
            </Bullet>
            <Bullet>
              <strong className="font-semibold">Sensitive Personal Identity Documents:</strong>{" "}
              Retained for no more than 1 year from the date of submission
            </Bullet>
            <Bullet>
              <strong className="font-semibold">Biometric Data:</strong> Retained for no more than 1
              year from the date of collection
            </Bullet>
          </ul>
          <p className="mt-4">
            After these retention periods expire, the respective information will be securely
            deleted from our systems unless we are required by law to retain it for a longer period.
          </p>

          <h3 className="mt-6 text-lg font-semibold text-foreground">8.2 Account Deletion</h3>
          <p className="mt-2">
            Users can request complete data deletion by deleting their account. This action will:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>Remove all personal information records from our systems</Bullet>
            <Bullet>
              Delete all associated identity documents and verification data (subject to the
              retention periods above for accounting records if applicable)
            </Bullet>
            <Bullet>
              <strong className="font-semibold">Cannot be undone</strong> - this is a permanent
              action
            </Bullet>
          </ul>
          <p className="mt-4">
            If you have shared your information with one of our SaaS providers, contact them for
            deletion of your information. If you have questions about this, please contact us using
            the information provided in Section 12.
          </p>
        </>
      ),
    },
    {
      title: "Your Rights and Choices",
      content: (
        <>
          <p>
            Depending on your location, you may have certain rights regarding your personal
            information, including:
          </p>
          <ul role="list" className="mt-4 ml-4 max-w-xl space-y-2">
            <Bullet>The right to access the personal information we hold about you</Bullet>
            <Bullet>The right to request correction of inaccurate information</Bullet>
            <Bullet>
              The right to request deletion of your information (subject to legal retention
              requirements)
            </Bullet>
            <Bullet>
              The right to withdraw consent where we rely on consent to process your information
            </Bullet>
          </ul>
          <p className="mt-4">
            To exercise these rights, please contact us using the information in Section 12.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            {entity} is not intended for children under the age of 13, and we do not knowingly
            collect information from children under 13.
          </p>
          <p className="mt-4">
            To create an account as a business customer, you must be at least 16 years old.
          </p>
          <p className="mt-4">
            For identity verification services, users must be at least 13 years old. For minors
            under the age of majority in their jurisdiction, a parent or legal guardian must submit
            the information on their behalf and consent to the use of that information for age
            verification purposes.
          </p>
          <p className="mt-4">
            If we become aware that we have collected information from a child under 13 without
            proper parental consent, we will take steps to delete that information.
          </p>
        </>
      ),
    },
    {
      title: "International Users",
      content: (
        <div className="space-y-4">
          <p>
            {entity} is located in Nevada, United States, but we serve users worldwide. If you are
            accessing our services from outside the United States, please be aware that your
            information may be transferred to, stored, and processed in the United States, where our
            servers and operations are located.
          </p>
          <p>
            By using our services, you consent to the transfer of your information to the United
            States and processing in accordance with this Privacy Policy and applicable U.S. laws.
          </p>
          <p>
            If you are located in the European Union, European Economic Area, United Kingdom, or
            other regions with data protection laws, you may have additional rights under those
            laws.
          </p>
        </div>
      ),
    },
    {
      title: "Contact Us",
      content: (
        <>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our
            privacy practices, please contact us at:
          </p>
          <figure className="mt-6 border-l border-primary pl-9">
            <blockquote className="font-semibold">
              <p>{contact.website}</p>
              <p>{contact.phone}</p>
              <p>{contact.fax}</p>
              <p>{contact.email}</p>
              <p>{contact.address}</p>
            </blockquote>
          </figure>
        </>
      ),
    },
    {
      title: "Changes to This Privacy Policy",
      content: (
        <div className="space-y-4">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technology, legal requirements, or other factors. When we make changes, we will update
            the "Effective Date" at the top of this policy.
          </p>
          <p>
            We encourage you to review this Privacy Policy periodically. If we make material
            changes, we will provide notice through our website or by other means as appropriate.
          </p>
          <p>
            Your continued use of our services after changes to this Privacy Policy constitutes your
            acceptance of the updated policy.
          </p>
        </div>
      ),
    },
  ],
} satisfies PolicyData;

function PolicySection({ n, s }: { n: number; s: PolicySection }) {
  return (
    <section className="mb-8 space-y-4">
      <h2 className="text-xl font-semibold text-foreground">
        {n}. {s.title}
      </h2>
      <div className="text-muted-foreground">
        {typeof s.content === "string" ? <p>{s.content}</p> : s.content}
      </div>
    </section>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start justify-items-center gap-x-3">
      <CircleDot
        aria-hidden="true"
        width="4"
        height="4"
        className="mt-1 size-4 flex-none text-primary"
      />
      <span>{children}</span>
    </li>
  );
}
